import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Cog, Plus, Loader2, Edit, Wrench, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_OPTS = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'manutencao', label: 'Em manutenção' },
  { value: 'parado', label: 'Parado' },
];

const UNIDADES_CAP = ['kg/h', 'un/h', 'L/h', 'kg', 'un', 'L'];

const emptyEquip = () => ({
  tipo_equipamento_id: '',
  apelido: '',
  fabricante: '',
  modelo: '',
  capacidade_valor: '',
  capacidade_unidade: 'kg/h',
  data_aquisicao: '',
  vida_util_anos: '',
  tag_patrimonio: '',
  setor: '',
  setor_id: '',
  observacoes: '',
  status: 'ativo',
});

const emptyPlano = () => ({
  titulo: '',
  descricao: '',
  periodicidade_tipo: 'calendario',
  intervalo_dias: 30,
  intervalo_horas_uso: '',
});

const EquipamentosPage = () => {
  const { getEmpresaId, hasPermission } = useAuth();
  const [equipamentos, setEquipamentos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyEquip());
  const [planos, setPlanos] = useState([]);
  const [planoForm, setPlanoForm] = useState(emptyPlano());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const empresa_id = getEmpresaId();
      const [rEq, rTipos, rSetores] = await Promise.all([
        httpsCallable(functions, 'listEquipamentos')({ empresa_id }),
        httpsCallable(functions, 'listTiposEquipamento')({ apenas_ativos: true }),
        httpsCallable(functions, 'listSetores')({ empresa_id, apenas_ativos: true }),
      ]);
      setEquipamentos(rEq.data?.data?.items || []);
      setTipos(rTipos.data?.data?.items || []);
      setSetores(rSetores.data?.data?.items || []);
    } catch (e) {
      setErr(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [getEmpresaId]);

  const openNew = () => {
    setEditId(null);
    setForm(emptyEquip());
    setPlanos([]);
    setPlanoForm(emptyPlano());
    setModal(true);
  };

  const openEdit = async (eq) => {
    setEditId(eq.id);
    setForm({
      tipo_equipamento_id: eq.tipo_equipamento_id || '',
      apelido: eq.apelido || '',
      fabricante: eq.fabricante || '',
      modelo: eq.modelo || '',
      capacidade_valor: eq.capacidade_valor ?? '',
      capacidade_unidade: eq.capacidade_unidade || 'kg/h',
      data_aquisicao: eq.data_aquisicao?.toDate
        ? eq.data_aquisicao.toDate().toISOString().slice(0, 10)
        : eq.data_aquisicao
        ? String(eq.data_aquisicao).slice(0, 10)
        : '',
      vida_util_anos: eq.vida_util_anos ?? '',
      tag_patrimonio: eq.tag_patrimonio || '',
      setor: eq.setor_nome || eq.setor || '',
      setor_id: eq.setor_id || '',
      observacoes: eq.observacoes || '',
      status: eq.status || 'ativo',
    });
    try {
      const r = await httpsCallable(functions, 'getEquipamento')({
        empresa_id: getEmpresaId(),
        id: eq.id,
      });
      setPlanos(r.data?.data?.planos || []);
    } catch {
      setPlanos([]);
    }
    setModal(true);
  };

  const saveEquip = async () => {
    setSaving(true);
    setErr('');
    try {
      const payload = {
        empresa_id: getEmpresaId(),
        ...form,
        capacidade_valor: Number(form.capacidade_valor) || 0,
        vida_util_anos: form.vida_util_anos ? Number(form.vida_util_anos) : null,
        data_aquisicao: form.data_aquisicao || null,
      };
      if (editId) {
        await httpsCallable(functions, 'updateEquipamento')({ id: editId, ...payload });
        setMsg('Equipamento atualizado.');
      } else {
        const r = await httpsCallable(functions, 'createEquipamento')(payload);
        setEditId(r.data?.data?.id);
        setMsg('Equipamento criado. Você pode adicionar planos de manutenção abaixo.');
      }
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const excluirEquipamento = async (eq) => {
    const nome = eq.apelido || eq.id;
    if (
      !window.confirm(
        `Excluir o equipamento "${nome}"?\n\nOs planos de manutenção serão desativados. O histórico de uso e manutenções executadas é mantido.`
      )
    ) {
      return;
    }
    setDeletingId(eq.id);
    setErr('');
    try {
      await httpsCallable(functions, 'deleteEquipamento')({
        empresa_id: getEmpresaId(),
        id: eq.id,
      });
      if (editId === eq.id) {
        setModal(false);
        setEditId(null);
      }
      setMsg(`Equipamento "${nome}" excluído.`);
      await load();
    } catch (e) {
      setErr(e.message || 'Erro ao excluir equipamento');
    } finally {
      setDeletingId(null);
    }
  };

  const addPlano = async () => {
    if (!editId) {
      setErr('Salve o equipamento antes de adicionar planos.');
      return;
    }
    try {
      await httpsCallable(functions, 'createPlanoManutencao')({
        empresa_id: getEmpresaId(),
        equipamento_id: editId,
        ...planoForm,
        intervalo_dias: Number(planoForm.intervalo_dias) || 0,
        intervalo_horas_uso: planoForm.intervalo_horas_uso ? Number(planoForm.intervalo_horas_uso) : 0,
      });
      setPlanoForm(emptyPlano());
      const r = await httpsCallable(functions, 'getEquipamento')({
        empresa_id: getEmpresaId(),
        id: editId,
      });
      setPlanos(r.data?.data?.planos || []);
      setMsg('Plano de manutenção adicionado.');
    } catch (e) {
      setErr(e.message || 'Erro ao criar plano');
    }
  };

  if (!hasPermission('manage_equipment')) {
    return <div className="p-6 text-center text-gray-600">Sem permissão.</div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Cog className="h-7 w-7 text-blue-600" />
            Equipamentos
          </h1>
          <p className="text-sm text-gray-600 mt-1">Ativos da fábrica vinculados aos tipos globais do sistema.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/plano-manutencao" className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1">
            <Wrench className="h-4 w-4" /> Plano de Manutenção
          </Link>
          <button type="button" onClick={openNew} className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2">
            <Plus className="h-4 w-4" /> Novo equipamento
          </button>
        </div>
      </div>

      {msg && <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{msg}</div>}
      {err && <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {equipamentos.map((eq) => (
            <div key={eq.id} className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900">{eq.apelido}</h3>
                  <p className="text-xs text-gray-500">{eq.tipo_nome}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(eq)}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    title="Editar"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => excluirEquipamento(eq)}
                    disabled={deletingId === eq.id}
                    className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                    title="Excluir"
                  >
                    {deletingId === eq.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {eq.fabricante} {eq.modelo}
              </p>
              <p className="text-sm mt-1">
                Capacidade: <strong>{eq.capacidade_valor}</strong> {eq.capacidade_unidade}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Setor: {eq.setor_nome || eq.setor || '—'} · Horas de uso:{' '}
                {Number(eq.horas_uso_acumuladas || 0).toLocaleString('pt-BR')} h · Status: {eq.status}
              </p>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 my-8 space-y-4">
            <h2 className="text-lg font-semibold">{editId ? 'Editar equipamento' : 'Novo equipamento'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-500">Tipo (global)</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.tipo_equipamento_id}
                  onChange={(e) => setForm({ ...form, tipo_equipamento_id: e.target.value })}
                >
                  <option value="">Selecione…</option>
                  {tipos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome} — {t.nivel1_area}
                    </option>
                  ))}
                </select>
              </div>
              {[
                ['apelido', 'Apelido / nome na fábrica'],
                ['fabricante', 'Fabricante'],
                ['modelo', 'Modelo'],
                ['tag_patrimonio', 'Tag / patrimônio'],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-gray-500">{label}</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-500">Setor da fábrica</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.setor_id}
                  onChange={(e) => {
                    const sid = e.target.value;
                    const s = setores.find((x) => x.id === sid);
                    setForm({
                      ...form,
                      setor_id: sid,
                      setor: s?.nome || '',
                    });
                  }}
                >
                  <option value="">Sem setor / selecione…</option>
                  {setores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nome}
                    </option>
                  ))}
                </select>
                {setores.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    Cadastre setores em Admin → Setores para organizar os equipamentos.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Capacidade</label>
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.capacidade_valor}
                  onChange={(e) => setForm({ ...form, capacidade_valor: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Unidade</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.capacidade_unidade}
                  onChange={(e) => setForm({ ...form, capacidade_unidade: e.target.value })}
                >
                  {UNIDADES_CAP.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Data de aquisição</label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.data_aquisicao}
                  onChange={(e) => setForm({ ...form, data_aquisicao: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Vida útil prevista (anos)</label>
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.vida_util_anos}
                  onChange={(e) => setForm({ ...form, vida_util_anos: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Status</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {STATUS_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {editId && (
              <div className="border-t pt-4 space-y-3">
                <h3 className="font-medium text-gray-900">Planos de manutenção</h3>
                <ul className="text-sm space-y-1">
                  {planos.map((p) => (
                    <li key={p.id} className="text-gray-700">
                      • {p.titulo} ({p.periodicidade_tipo})
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-gray-50 p-3 rounded-lg">
                  <input
                    className="border rounded px-2 py-1 text-sm sm:col-span-2"
                    placeholder="Título do plano"
                    value={planoForm.titulo}
                    onChange={(e) => setPlanoForm({ ...planoForm, titulo: e.target.value })}
                  />
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={planoForm.periodicidade_tipo}
                    onChange={(e) => setPlanoForm({ ...planoForm, periodicidade_tipo: e.target.value })}
                  >
                    <option value="calendario">Calendário</option>
                    <option value="horas">Horas de uso</option>
                    <option value="ambos">Calendário e horas</option>
                  </select>
                  <input
                    type="number"
                    className="border rounded px-2 py-1 text-sm"
                    placeholder="Intervalo (dias)"
                    value={planoForm.intervalo_dias}
                    onChange={(e) => setPlanoForm({ ...planoForm, intervalo_dias: e.target.value })}
                  />
                  <input
                    type="number"
                    className="border rounded px-2 py-1 text-sm"
                    placeholder="Intervalo (horas)"
                    value={planoForm.intervalo_horas_uso}
                    onChange={(e) => setPlanoForm({ ...planoForm, intervalo_horas_uso: e.target.value })}
                  />
                  <button type="button" onClick={addPlano} className="text-sm text-blue-600 hover:underline sm:col-span-2 text-left">
                    + Adicionar plano
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between gap-2 flex-wrap">
              {editId ? (
                <button
                  type="button"
                  disabled={deletingId === editId || saving}
                  onClick={() => excluirEquipamento({ id: editId, apelido: form.apelido })}
                  className="px-3 py-2 text-red-700 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                >
                  {deletingId === editId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  Excluir equipamento
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2 ml-auto">
                <button type="button" onClick={() => setModal(false)} className="px-3 py-2 border rounded-lg">
                  Fechar
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveEquip}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipamentosPage;
